import { useQuery } from '@apollo/client';
import CatalogForm from '@nile/banyan/src/Pages/ServiceCatalog/CatalogForm';
import {
  getParsedCrudData,
  Loader,
  CustomModal,
  CardPage,
  Tag,
  getTranslatedText,
  SuperPop,
  toast,
  useSiteListForDropdown
} from '@nile/components';
import { Page, ButtonGroup, Button } from '@superops/ui-design-system';
import { AccountClient, GET_SERVICE_ITEM } from '@nile/graphql';
import React, {
  Component,
  createContext,
  createRef,
  useCallback,
  useEffect,
  useState
} from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import { omit, pick } from 'lodash';
import dayjs from 'dayjs';
import { ContentWrapper } from 'sopslibrary';
import styled from 'styled-components';
import AssetView, {
  AssetViewToolbar
} from '@nile/client_management/src/Pages/ContractTemplate/AssetView';

const NameWrap = styled.div`
  color: blue;
  width: ${(props) => (props.hasTag ? 'calc(100% - 100px)' : '100%')};
  text-overflow: ellipsis;
  overflow: hidden;
`;

const Wrapper = styled.div`
  height: 100%;
  min-width: 1200px;
  max-width: 1200px;
  background: #fff;
  border-radius: 10px;
`;

const CatalogFormTag = styled(ContentWrapper)`
  gap: 40px;
`;

export const ServiceItemSheetContext = createContext();

function ServiceItemSheet(props) {
  const {
    intl,
    item,
    updateItem,
    configurableSections,
    disabledSections,
    mode,
    clientId,
    canEdit,
    canEditEndDate,
    totalAssets,
    totalUsers
  } = props;

  const { quantity, quantityType, unitType } = item;
  const [sheetActive, setSheetActive] = useState(false);
  const [customModal, setCustomModal] = useState(false);
  const toggleSheetActive = useCallback(() => {
    setSheetActive(!sheetActive);
  });
  const closeModal = useCallback(() => {
    setCustomModal(false);
  });
  const { loading, data: siteList, getClientSites } = useSiteListForDropdown();
  useEffect(() => {
    if (clientId) {
      getClientSites(clientId);
    }
  }, [clientId]);

  const {
    loading: serviceItemLoading,
    error: serviceItemError,
    data: serviceItemData
  } = useQuery(GET_SERVICE_ITEM, {
    client: AccountClient,
    fetchPolicy: 'network-only',
    variables: { itemId: item.serviceItem.itemId }
  });

  const overrideServiceItem = (params) => {
    let parsedBlockHourObj = {};
    const parsedSiteObj = getParsedCrudData(
      params.sites || [],
      params.selectedSites
        ? params.selectedSites.filter((el) => el.id !== 'ALL_SITES')
        : []
    );
    if (mode === 'create') {
      parsedBlockHourObj = getParsedCrudData(
        [],
        params.selectedBlockItemAdjustedItems,
        'itemId'
      );
    } else {
      parsedBlockHourObj = getParsedCrudData(
        params.blockItemAdjustedItems || [],
        params.selectedBlockItemAdjustedItems,
        'itemId'
      );
    }
    const addedSites = parsedSiteObj.addedItems.map((el) => {
      return {
        id: el.id,
        name: el.name
      };
    });
    const deletedSites = parsedSiteObj.deletedItems.map((el) => {
      return {
        id: el.id,
        name: el.name
      };
    });
    let addBlockItemAdjustedItems = [];
    let deleteBlockItemAdjustedItems = [];
    if (parsedBlockHourObj.addedItems) {
      addBlockItemAdjustedItems = parsedBlockHourObj.addedItems.map((el) => {
        return {
          itemId: el.itemId,
          name: el.name
        };
      });
    }
    if (parsedBlockHourObj.deletedItems) {
      deleteBlockItemAdjustedItems = parsedBlockHourObj.deletedItems.map(
        (el) => {
          return {
            itemId: el.itemId,
            name: el.name
          };
        }
      );
    }
    const inputParams = {
      itemId: item.itemId || item.serviceItem.itemId,
      frequency: params.frequency || {
        type: null,
        unit: null,
        quantity: null
      },
      // subscriptionType: params.subscriptionType || null,
      unitPrice: params.unitPrice || null,
      amount: params.amount || null,
      description: params.invoiceDescription || null,
      unitType: params.unitType || null,
      quantity: params.quantity || null,
      effectiveDate: params.effectiveDate || null,
      billDate: params.billDate || null, //todo omit this in contractsheet
      endDate: params.endDate || null,
      carryForwardRemainingBlockHours:
        params.carryForwardRemainingBlockHours || null,
      blockItemUsedIn: params.blockItemUsedIn || null,
      useAsWorklogItem: params.useAsWorklogItem || false,
      addBlockItemAdjustedItems: addBlockItemAdjustedItems || [],
      deleteBlockItemAdjustedItems: deleteBlockItemAdjustedItems || [],
      autoRenew: params.autoRenew || null,
      addSites: addedSites,
      deleteSites: deletedSites,
      sites: params.selectedSites,
      adjustBlockItemAgainstAllItems:
        params.adjustBlockItemAgainstAllItems || false,
      change: params.change || null,
      unitPriceOverridden: params.unitPriceOverridden,
      assetView:
        params.unitType === 'ASSET_VIEW' ? { id: params.assetView?.id } : null
    };
    if (!['BLOCK_HOURS', 'BLOCK_MONEY'].includes(params.quantityType)) {
      inputParams.blockItemUsedIn = null;
    }
    if (!['UNITS'].includes(params.quantityType)) {
      inputParams.unitType = null;
    }
    if (props.enableReminder) {
      inputParams.remindDuration =
        params?.endDate && params?.remindDuration
          ? params.remindDuration
          : null;
    }
    props.updateItem(inputParams);
  };

  if (loading || serviceItemLoading) return <Loader />;
  const allServiceItemData = serviceItemData?.getServiceItem || {};
  const mergedServiceItemData = {
    ...item,
    serviceItem: {
      ...allServiceItemData,
      assetView: item.assetView || null,
      adjustBlockItemAgainstAllItems:
        item.adjustBlockItemAgainstAllItems || false,
      amount: item.amount || allServiceItemData.amount || null,
      invoiceDescription:
        item.description || allServiceItemData.invoiceDescription || null,
      frequency: item.frequency ||
        allServiceItemData.frequency || {
          type: null,
          unit: null,
          quantity: null,
          recurringMode: null,
          recurringBefore: null
        },
      // subscriptionType:
      //   item.subscriptionType || allServiceItemData.subscriptionType || null,
      unitPrice: item.unitPrice || allServiceItemData.unitPrice || null,
      unitType: item.unitType || allServiceItemData.unitType || null,
      quantity: item.quantity || allServiceItemData.quantity || null,
      effectiveDate:
        item.effectiveDate || allServiceItemData.effectiveDate || null,
      billDate: item.billDate || allServiceItemData.billDate || null,
      nextBillDate:
        item.nextBillDate || allServiceItemData.nextBillDate || null,
      endDate: item.endDate || allServiceItemData.endDate || null,
      carryForwardRemainingBlockHours:
        item.carryForwardRemainingBlockHours ||
        allServiceItemData.carryForwardRemainingBlockHours ||
        null,
      blockItemUsedIn:
        item.blockItemUsedIn || allServiceItemData.blockItemUsedIn || null,
      useAsWorklogItem:
        item.useAsWorklogItem || allServiceItemData.useAsWorklogItem || false,
      blockItemAdjustedItems:
        item.blockItemAdjustedItems ||
        allServiceItemData.blockItemAdjustedItems ||
        [],
      addBlockItemAdjustedItems:
        item.addBlockItemAdjustedItems ||
        allServiceItemData.addBlockItemAdjustedItems ||
        [],
      deleteBlockItemAdjustedItems:
        item.deleteBlockItemAdjustedItems ||
        allServiceItemData.deleteBlockItemAdjustedItems ||
        [],
      autoRenew: item.autoRenew || allServiceItemData.autoRenew || null,
      sites: item.sites || null,
      changes: item.changes && item.changes.length ? item.changes : null,
      change: item.change || allServiceItemData?.change || null,
      unitPriceOverridden: item.unitPriceOverridden,
      integrationAddOn:
        item?.serviceItem?.integrationAddOn ||
        allServiceItemData?.integrationAddOn ||
        null
    }
  };
  if (props.enableReminder) {
    mergedServiceItemData.serviceItem.remindDuration =
      item.remindDuration || null;
  }

  return (
    <div className="flex center mw-100">
      <div
        title={item.serviceItem.name}
        className="flex actr cursor-pointer w100 ellipsis"
      >
        <NameWrap
          className="link"
          onClick={() => {
            setCustomModal(true);
          }}
          hasTag={item.serviceItem?.integrationState?.pax8}
        >
          {item.serviceItem.name}
        </NameWrap>
        {item.serviceItem?.integrationState?.pax8 && (
          <SuperPop
            position="top-start"
            content={getTranslatedText(
              intl,
              'client.contract.serviceItem.pax8.superTip'
            )}
            holder={
              <Tag className="ml-20 uppercase" size="small">
                <FormattedMessage id="app.integrations.pax8" />
              </Tag>
            }
          />
        )}
      </div>
      {customModal && (
        <CustomModal open={customModal} onClose={closeModal}>
          <div className="vh90">
            <ServiceItem
              serviceItem={mergedServiceItemData}
              overrideServiceItem={overrideServiceItem}
              toggleSheetActive={closeModal}
              configurableSections={configurableSections}
              disabledSections={disabledSections}
              siteList={siteList || []}
              canEdit={canEdit}
              canEditEndDate={canEditEndDate}
              totalAssets={totalAssets}
              totalUsers={totalUsers}
              clientId={clientId}
              formType={props.formType}
              enableReminder={props.enableReminder}
              isSiteSpecificEnabled={props.isSiteSpecificEnabled}
              refetchSubscriptionQuantity={props.refetchSubscriptionQuantity}
              subscriptionItemQuantityCount={
                props.subscriptionItemQuantityCount
              }
            />
          </div>
        </CustomModal>
      )}
    </div>
  );
}

export class ServiceItem extends Component {
  constructor(props) {
    super(props);

    this.assetViewFormRef = createRef();

    this.state = {
      serviceItem: props.serviceItem.serviceItem
        ? {
            ...props.serviceItem.serviceItem,
            selectedBlockItemAdjustedItems:
              props.serviceItem.blockItemAdjustedItems ||
              props.serviceItem.serviceItem.blockItemAdjustedItems ||
              [],
            selectedSites: props.serviceItem.serviceItem.sites || null,
            blockItemUsedIn:
              props.serviceItem.blockItemUsedIn || 'BUSINESS_HOURS',
            unitType: props.serviceItem.unitType || 'CUSTOM'
          }
        : {
            name: null,
            invoiceDescription: null,
            quantityType: 'UNITS',
            // subscriptionType: null,
            frequency: {
              type: null,
              unit: null,
              quantity: null,
              recurringMode: null,
              recurringBefore: null
            },
            hoursType: null,
            roundUpValue: null,
            unitPrice: null,
            category: {
              name: null
            },
            businessHoursUnitPrice: null,
            afterHoursUnitPrice: null,
            useAsWorklogItem: null,
            quantity: null,
            blockItemUsedIn: 'BUSINESS_HOURS',
            selectedBlockItemAdjustedItems: [],
            addBlockItemAdjustedItems: [],
            deleteBlockItemAdjustedItems: [],
            blockItemAdjustedItems: [],
            carryForwardRemainingBlockHours: false,
            autoRenew: false,
            selectedSites: null,
            assetView: null
          },
      errors: null,
      showAssetViewPage: false,
      selectedEditAssetViewData: null,
      assetViewSaveLoading: false,
      hasAssetViewConditionError: false,
      totalAssetsInPreview: null
    };
  }

  /**
   * Method passed through context to update properties
   */
  updateProperties = (properties) => {
    this.setState({
      serviceItem: {
        ...this.state.serviceItem,
        ...properties
      }
    });
  };

  overrideServiceItem = () => {
    const { serviceItem } = this.state;
    const { formType, canEdit, canEditEndDate } = this.props;
    if (serviceItem?.unitType === 'ASSET_VIEW' && !serviceItem?.assetView) {
      this.setState({ hasAssetViewConditionError: true });
      return;
    }
    this.setState({ hasAssetViewConditionError: false });

    if (
      (!serviceItem?.integrationAddOn &&
      !this.props.saveAction && // TODO - vignesh remove this check - Did for removing validation in integration
        formType !== 'service-catalog' &&
        formType !== 'contract-template') ||
      (this.props.entryPoint === 'INTEGRATION' &&
        serviceItem.unitType !== 'INTEGRATION_USAGE')
    ) {
      if (!serviceItem?.changes) {
        if (!serviceItem.change) {
          this.setState({ errors: true });
          return;
        }
      }
      if (serviceItem?.change) {
        if (serviceItem?.quantityType === 'UNITS') {
          if (serviceItem.unitType === 'CUSTOM') {
            if (!serviceItem.change.quantity) {
              this.setState({ errors: true });
              return;
            }
          }
        } else if (serviceItem?.quantityType === 'BLOCK_HOURS') {
          if (!serviceItem.change.quantity) {
            this.setState({ errors: true });
            return;
          }
        } else if (serviceItem?.quantityType === 'BLOCK_MONEY') {
          if (!serviceItem.change.amount) {
            this.setState({ errors: true });
            return;
          }
        }
      }
    }

    this.setState({ errors: false });
    if (this.props.saveAction) {
      this.setState({ isLoading: true });
      let inputParams = {};
      if (serviceItem.unitType === 'INTEGRATION_USAGE') {
        inputParams = {
          unitType: 'INTEGRATION_USAGE',
          itemId: this.props.serviceItem.itemId,
          unitPrice: serviceItem.unitPrice,
          billDate: dayjs()
            .date(1)
            .format('YYYY-MM-DD')
        };
      } else {
        if (canEditEndDate && !canEdit) {
          // If items other than end date are not editable, then send only end date in the input and skip other fields
          inputParams = {
            endDate: serviceItem.endDate
          };
          if (serviceItem.change) {
            if (serviceItem?.change?.quantity) {
              inputParams.change = serviceItem.change;
            } else {
              inputParams.change = {
                ...serviceItem.change,
                quantity: 0
              };
            }
          }
        }

        const inputParameters = { ...serviceItem };
        if (!inputParameters?.change) {
          inputParameters.change = undefined;
        }
        inputParams = {
          ...pick(inputParameters, [
            'billDate',
            'change',
            'subscriptionType',
            'unitPrice',
            'unitType'
          ]),
          itemId: this.props.serviceItem.itemId
        };
      }
      this.props
        .saveAction({
          variables: {
            input: inputParams
          }
        })
        .then((res) => {
          toast('success', 'client.contract.item.manageItem.success');
          this.props.onClose();
        })
        .finally(() => {
          this.setState({ isLoading: false });
        });
    } else {
      this.props.overrideServiceItem(this.state.serviceItem);
      this.props.toggleSheetActive();
    }
  };

  render() {
    const { serviceItem, errors } = this.state;
    const {
      configurableSections,
      disabledSections,
      canEdit,
      canEditEndDate,
      totalUsers,
      totalAssets,
      clientId,
      refetchSubscriptionQuantity,
      isSiteSpecificEnabled,
      subscriptionItemQuantityCount
    } = this.props;
    const context = {
      updateProperties: this.updateProperties
    };
    const WrapperTag = CatalogFormTag;
    const integrationAddOn =
      serviceItem?.integrationAddOn &&
      serviceItem?.unitType === 'INTEGRATION_USAGE';

    const onBack = () => {
      this.setState({
        showAssetViewPage: false,
        selectedEditAssetViewData: null
      });
    };

    const isEdit = Boolean(this.state.selectedEditAssetViewData);
    return (
      <ServiceItemSheetContext.Provider
        value={{
          setSelectedAssetView: (assetView) => {
            this.setState({
              serviceItem: { ...this.state.serviceItem, assetView }
            });
          },
          showAssetViewPage: this.state.showAssetViewPage,
          setShowAssetViewPage: (showAssetViewPage) =>
            this.setState({ showAssetViewPage }),
          selectedEditAssetViewData: this.state.selectedEditAssetViewData,
          setSelectedEditAssetViewData: (selectedEditAssetViewData) => {
            this.setState({
              selectedEditAssetViewData,
              showAssetViewPage: true
            });
          },
          hasAssetViewConditionError: this.state.hasAssetViewConditionError,
          totalAssetsInPreview: this.state.totalAssetsInPreview,
          setTotalAssetsInPreview: (totalAssetsInPreview) =>
            this.setState({ totalAssetsInPreview }),
          isSiteSpecificEnabled,
          clientId,
          serviceItem,
          isNewContractTemplate: this.props.formType === 'contract-template'
        }}
      >
        {!this.state.showAssetViewPage ? (
          <Wrapper>
            <Page
              variant="primary"
              title={
                <FormattedMessage id="contractTemplate.serviceItem.header" />
              }
              primaryAction={
                <ButtonGroup>
                  <Button
                    id={'contractServiceItemCancel'}
                    plainSecondary
                    onClick={() => {
                      if (this.props.onClose) {
                        this.props.onClose();
                      } else {
                        this.props.toggleSheetActive();
                      }
                    }}
                  >
                    <FormattedMessage id="common.cancel" />
                  </Button>
                  <Button
                    primary
                    id={'contractServiceItemSave'}
                    onClick={this.overrideServiceItem}
                    loading={this.state.isLoading}
                  >
                    <FormattedMessage id="contractTemplate.serviceItem.done" />
                  </Button>
                </ButtonGroup>
              }
              contentHeight="120px"
            >
              <CatalogForm
                tag={WrapperTag}
                serviceItem={serviceItem}
                context={context}
                configurableSections={configurableSections}
                disabledSections={disabledSections}
                siteList={this.props.siteList}
                disableAllSections={!canEdit && !integrationAddOn}
                disableEditEndDate={!canEditEndDate && !integrationAddOn}
                totalAssets={totalAssets}
                totalUsers={totalUsers}
                clientId={clientId}
                pax8={serviceItem?.integrationState?.pax8}
                formType={this.props.formType}
                enableReminder={this.props.enableReminder}
                errors={errors}
                integrationAddOn={integrationAddOn}
                isIntegrationAddOnItem={serviceItem?.integrationAddOn}
                entryPoint={this.props.entryPoint}
                isSiteSpecificEnabled={isSiteSpecificEnabled}
                refetchSubscriptionQuantity={refetchSubscriptionQuantity}
                subscriptionItemQuantityCount={subscriptionItemQuantityCount}
                contractServiceItemData={{
                  itemId: this.props.serviceItem?.itemId
                }}
              />
            </Page>
          </Wrapper>
        ) : (
          <Wrapper>
            <Page
              title={<AssetViewToolbar isEdit={isEdit} onBack={onBack} />}
              primaryAction={
                <div className="fx">
                  {this.state.totalAssetsInPreview > 0 && (
                    <Tag
                      className="ml8 mr8 pointer border-black"
                      background="#FFFEF0"
                    >
                      {this.state.totalAssetsInPreview}{' '}
                      <FormattedMessage id="admin.serviceCatalog.list.selected" />
                    </Tag>
                  )}
                  <ButtonGroup>
                    <Button plainSecondary onClick={onBack}>
                      <FormattedMessage id="common.cancel" />
                    </Button>
                    <Button
                      primary
                      onClick={async () => {
                        try {
                          this.setState({ assetViewSaveLoading: true });
                          await this.assetViewFormRef.current.validate();
                          // save successful
                          if (isEdit) {
                            this.setState({
                              selectedEditAssetViewData: null
                            });
                          }
                          this.setState({ showAssetViewPage: false });
                        } catch {
                          // has form errors
                        } finally {
                          this.setState({ assetViewSaveLoading: false });
                        }
                      }}
                      loading={this.state.assetViewSaveLoading}
                    >
                      <FormattedMessage
                        id={isEdit ? 'common.update' : 'common.confirm'}
                      />
                    </Button>
                  </ButtonGroup>
                </div>
              }
            >
              <AssetView
                isEdit={isEdit}
                id={this.state.selectedEditAssetViewData?.id}
                ref={this.assetViewFormRef}
              />
            </Page>
          </Wrapper>
        )}
      </ServiceItemSheetContext.Provider>
    );
  }
}

export default injectIntl(ServiceItemSheet);
